/* import __COLOCATED_TEMPLATE__ from './main.hbs'; */
/* RESPONSIBLE TEAM: team-standalone */

import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { type Tab } from 'embercom/components/standalone/common/tab-control';
import type FinStandaloneService from 'embercom/services/fin-standalone-service';
import type IntlService from 'ember-intl/services/intl';
import type RouterService from '@ember/routing/router-service';
import type SalesforceConfiguration from 'embercom/models/standalone/salesforce-configuration';
import { isPresent } from '@ember/utils';

export interface Args {
  activeStep: 'integrate' | 'configure-emails' | 'workflow' | 'assign' | 'go-live';
  openSection: string | null;
  setSection?: (section: string | null) => void;
}

export default class Main extends Component<Args> {
  @service declare finStandaloneService: FinStandaloneService;
  @service declare intl: IntlService;
  @service declare router: RouterService;
  @tracked selectedStep: Tab;

  constructor(owner: unknown, args: Args) {
    super(owner, args);
    this.selectedStep =
      this.deploySteps.find((step) => step.value === args.activeStep) ?? this.deploySteps[0];
  }

  get salesforceConfig(): SalesforceConfiguration | undefined {
    return this.finStandaloneService.salesforceConfig;
  }

  get salesforceSetupData() {
    return this.finStandaloneService.salesforceSetupData;
  }

  get isIntegrateStepComplete() {
    return (
      this.salesforceConfig?.isEnabled ||
      (this.salesforceConfig?.isConnected &&
        isPresent(this.salesforceConfig?.finSalesforceUserId) &&
        this.salesforceSetupData.hasPermissionSet &&
        this.salesforceSetupData.hasPermissionSetAssignedToInstaller)
    );
  }

  get isConfigureEmailsStepComplete() {
    return (
      this.salesforceConfig?.isEnabled ||
      (this.salesforceConfig?.isConnected &&
        isPresent(this.finStandaloneService.selectedSalesforceCaseOrigins) &&
        isPresent(this.salesforceConfig?.supportEmail) &&
        this.salesforceConfig?.isSupportDomainVerified)
    );
  }

  get visualBuilderObject() {
    return this.salesforceConfig?.ruleset.value?.rulesetLinks.firstObject.object
      .visualBuilderObject;
  }

  get isWorkflowStepComplete() {
    return (
      this.salesforceConfig?.isEnabled ||
      (this.salesforceConfig?.isConnected && this.visualBuilderObject?.validations.isValid)
    );
  }

  get deploySteps(): Array<Tab> {
    return [
      {
        stepNumber: 1,
        label: this.intl.t('standalone.deploy.salesforce.cases.tabs.integrate'),
        value: 'integrate',
        path: 'apps.app.standalone.deploy.salesforce.cases.integrate',
        isCompleted: this.isIntegrateStepComplete,
      },
      {
        stepNumber: 2,
        label: this.intl.t('standalone.deploy.salesforce.cases.tabs.configure-emails'),
        value: 'configure-emails',
        path: 'apps.app.standalone.deploy.salesforce.cases.configure-emails',
        isCompleted: this.isConfigureEmailsStepComplete,
      },
      {
        stepNumber: 3,
        label: this.intl.t('standalone.deploy.salesforce.cases.tabs.workflow'),
        value: 'workflow',
        path: 'apps.app.standalone.deploy.salesforce.cases.workflow',
        isCompleted: this.isWorkflowStepComplete,
      },
      {
        stepNumber: 4,
        label: this.intl.t('standalone.deploy.salesforce.cases.tabs.assign'),
        value: 'assign',
        path: 'apps.app.standalone.deploy.salesforce.cases.assign',
        isCompleted:
          this.salesforceConfig?.isEnabled ||
          (this.salesforceConfig?.isConnected && this.selectedStep?.stepNumber > 4),
      },
      {
        stepNumber: 5,
        label: this.intl.t('standalone.deploy.salesforce.cases.tabs.go-live'),
        value: 'go-live',
        path: 'apps.app.standalone.deploy.salesforce.cases.go-live',
        isCompleted: this.salesforceConfig?.isEnabled || false,
      },
    ];
  }

  @action
  setStep(stepValue: string) {
    let step = this.deploySteps.find((step) => step.value === stepValue);
    if (step) {
      this.selectedStep = step;
      if (step.path) {
        this.router.transitionTo(step.path);
      }
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Standalone::Deploy::Salesforce::Cases::Main': typeof Main;
    'standalone/deploy/salesforce/cases/main': typeof Main;
  }
}
